import { RootState } from 'constants/interfaces'
import { useSelector } from 'react-redux'
import localTranslations from 'config/translations'
import { ILiteral, LiteralParams, resolveLiteral } from 'utils/localeText'

export interface LocaleTextDefinition {
  key: string
  fallback?: Record<string, string>
}
export type LocaleTextDefinitions = Record<string, LocaleTextDefinition>

type ResolvedLocaleText<T extends LocaleTextDefinitions> = { [K in keyof T]: string }

function isLiteral(text: ILiteral | LocaleTextDefinitions): text is ILiteral {
  return typeof text.value === 'string' && typeof text.fallback === 'string'
}

function useLocaleText(text: string | ILiteral | null | undefined, isLocalUse?: boolean, params?: LiteralParams): string
function useLocaleText<T extends LocaleTextDefinitions>(definitions: T): ResolvedLocaleText<T>
function useLocaleText(
  text: string | ILiteral | LocaleTextDefinitions | null | undefined,
  isLocalUse = false,
  params?: LiteralParams
) {
  const translations = useSelector((state: RootState) => state.config?.translations)
  const locale = useSelector((state: RootState) => state.general.locale)

  // Async callers can render before their label is available; keep subscriptions stable while waiting.
  if (text == null) return ''

  if (typeof text !== 'string' && !isLiteral(text)) {
    return Object.keys(text).reduce<Record<string, string>>((result, name) => {
      const definition = text[name]
      result[name] = resolveLiteral(
        { value: definition.key, fallback: definition.fallback?.[locale] || definition.fallback?.en || definition.key },
        translations?.[locale]
      )
      return result
    }, {})
  }

  const key = typeof text === 'string' ? text : text.value
  const dictionary = isLocalUse
    ? { [key]: localTranslations[locale]?.[key] || localTranslations.base[key] }
    : translations?.[locale]
  return resolveLiteral(text, dictionary, params)
}

export default useLocaleText
